import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { Router } from '@angular/router';

import { IconLibrary, SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { symbolToLangPath, urlToPathname } from '@fcom/core/utils';
import { FinnairTheme } from '@fcom/common/interfaces/theme.interface';
import { FinnairThemeService } from '@fcom/common/services/theme/finnair-theme.service';
import { LanguageService } from '@fcom/ui-translate/services/language.service';

import { LinkSize, LinkTheme } from './enums';
import { ButtonType } from '../buttons/button/enums';
import { IconPosition } from '../icons';
import { AriaOptions } from '../interfaces';
import { LinkIcon } from './interfaces';

@Component({
  selector: 'fcom-link',
  templateUrl: './link.component.html',
  styleUrl: './link.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class LinkComponent {
  private readonly languageService = inject(LanguageService);
  private readonly router = inject(Router);
  private readonly themeService = inject(FinnairThemeService);

  readonly IconPosition = IconPosition;
  readonly LinkSize = LinkSize;
  readonly LinkTheme = LinkTheme;

  /**
   * Whether the new Finnair brand is active. Under the new brand the link renders in
   * Finnair Text Medium (see the `new-brand` style block); legacy is unchanged.
   */
  readonly isNewBrand = computed<boolean>(() => this.themeService.siteTheme() !== FinnairTheme.LEGACY);

  /**
   * The size of the link
   */
  size = input<LinkSize>(LinkSize.MEDIUM);

  /**
   * Different themes for dark and light backgrounds
   */
  theme = input<LinkTheme>(LinkTheme.LIGHT);

  /**
   * Url of the link. If missing, component will be rendered as button.
   */
  href = input<string>();

  /**
   * Flag to indicate whether to convert url to pathName for router
   */
  external = input<boolean>();

  /**
   * Whether to open link in a new tab
   */
  openInNewTab = input<boolean>();

  /**
   * Adds a nofollow rel for the link
   */
  nofollow = input<boolean>();

  /**
   * Flag to disable the button. Doesn't have any effect on regular link.
   */
  disabled = input<boolean>();

  /**
   * Button type. Doesn't have any effect on regular link.
   */
  type = input<ButtonType>(ButtonType.BUTTON);

  /**
   * ARIA properties
   */
  aria = input<AriaOptions>();

  truncate = input(false);

  /**
   * Icon for the link. Defaults to external-tab icon for external links.
   */
  icon = input<LinkIcon>();

  readonly effectiveIcon = computed<LinkIcon | undefined>(() => {
    const explicit = this.icon();
    if (explicit) {
      return explicit;
    }
    if (this.external()) {
      return {
        name: SvgLibraryIcon.EXTERNAL_TAB,
        category: IconLibrary.SVG_LIBRARY,
        position: IconPosition.RIGHT,
      };
    }
    return undefined;
  });

  readonly iconClasses = computed<Record<string, boolean>>(() => {
    const icon = this.effectiveIcon();
    const extraClasses = icon?.extraClasses;
    return {
      'ms-xxsmall-r': icon?.position === IconPosition.LEFT,
      'ms-xxsmall-l': icon?.position === IconPosition.RIGHT,
      'icon-medium': this.size() === LinkSize.MEDIUM,
      'icon-small': this.size() === LinkSize.SMALL,
      ...(extraClasses ? { [extraClasses]: true } : {}),
    };
  });

  afterNavigation = output<Event>();
  beforeNavigation = output<Event>();

  readonly rel = computed<string | undefined>(() => {
    const parts = [this.openInNewTab() && 'noopener', this.nofollow() && 'nofollow'].filter(Boolean);
    return parts.length ? parts.join(' ') : undefined;
  });

  readonly target = computed<string | undefined>(() => (this.openInNewTab() ? '_blank' : undefined));

  readonly linkVal = computed<string | undefined>(() => {
    const href = this.href();
    if (!href) {
      return undefined;
    }
    const localized = this.legacySiteLinkLocale(href);
    return this.external() ? localized : urlToPathname(localized, { preserveQueryParams: true });
  });

  handleClick(event: MouseEvent): void {
    this.beforeNavigation.emit(event);
    if (
      !(event.ctrlKey || event.metaKey) &&
      !this.external() &&
      !this.openInNewTab() &&
      !event.defaultPrevented &&
      !this.disabled()
    ) {
      event.stopPropagation();
      event.preventDefault();
      if (this.linkVal()) {
        this.router.navigateByUrl(this.linkVal());
      }
      this.afterNavigation.emit(event);
    }
  }

  legacySiteLinkLocale(url = ''): string {
    if (!url) {
      return url;
    }
    const ddsLocale = this.languageService.ddsLocaleValue;
    return symbolToLangPath(url, ddsLocale as Parameters<typeof symbolToLangPath>[1]);
  }
}
